<script lang="ts">
export default {
  name: 'AppModal',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    show: boolean;
    title?: string;
    cancelButtonLabel?: string;
    actionButtonLabel?: string;
    handleCancel?: () => void;
    handleAction?: () => void;
    loading?: boolean;
    actionDisabled?: boolean;
    modalClass?: string;
    disableTeleport?: boolean;
    showOverflow?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    layout?: 'single' | 'two-column';
  }>(),
  {
    disableTeleport: false,
    showOverflow: false,
    size: 'md',
    layout: 'single',
  },
);

const dialogRef = ref<HTMLDialogElement | null>(null);

const cancelButtonLabel = computed(() => props.cancelButtonLabel ?? 'Cancel');
const actionButtonLabel = computed(() => props.actionButtonLabel ?? 'Save');
const handleCancel = computed(
  () =>
    props.handleCancel ??
    (() => {
      console.log('cancel');
    }),
);
const handleAction = computed(
  () =>
    props.handleAction ??
    (() => {
      console.log('action');
    }),
);

// Modal sizing computed properties
const modalSizeClasses = computed(() => {
  const sizeMap = {
    sm: 'tw:max-w-md',
    md: 'tw:max-w-lg',
    lg: 'tw:max-w-2xl',
    xl: 'tw:max-w-4xl',
  };
  return sizeMap[props.size];
});

const contentLayoutClasses = computed(() => {
  if (props.layout === 'two-column') {
    return 'tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6';
  }
  return '';
});

function open() {
  dialogRef.value?.showModal();
}

function close() {
  dialogRef.value?.close();
}

defineExpose({
  open,
  close,
  dialogRef,
});
</script>

<template>
  <Teleport
    to="body"
    :disabled="disableTeleport">
    <dialog
      class="tw:modal tw:z-1100 tw:items-center"
      :class="{ 'tw:modal-open': show }"
      ref="dialogRef">
      <div
        class="tw:form-control tw:modal-box tw:flex tw:max-h-[90vh] tw:flex-col tw:gap-4 tw:overflow-hidden tw:rounded-xl"
        :class="[modalSizeClasses, modalClass ?? '', { 'tw:overflow-visible': showOverflow }]">
        <div class="tw:flex tw:shrink-0 tw:flex-col">
          <slot name="title">
            <h1 v-if="title">{{ title }}</h1>
          </slot>
        </div>
        <div
          class="tw:min-h-0 tw:w-full tw:flex-1"
          :class="[{ 'tw:overflow-y-auto': !showOverflow }, contentLayoutClasses]">
          <!-- Single column layout -->
          <template v-if="layout === 'single'">
            <slot name="content"></slot>
          </template>

          <!-- Two column layout -->
          <template v-else-if="layout === 'two-column'">
            <div class="tw:space-y-4">
              <slot name="left-column"></slot>
            </div>
            <div class="tw:space-y-4">
              <slot name="right-column"></slot>
            </div>
          </template>
        </div>
        <div class="tw:modal-action tw:shrink-0 tw:items-center">
          <slot name="extra-actions"> </slot>

          <slot name="actions">
            <button
              class="tw:btn tw:btn-sm"
              @click="handleCancel">
              {{ cancelButtonLabel }}
            </button>
            <button
              class="tw:btn tw:text-white tw:btn-sm tw:btn-primary"
              :class="{ 'tw:btn-disabled': actionDisabled }"
              @click="handleAction">
              <span
                v-if="loading"
                class="tw:loading tw:mx-2 tw:loading-sm tw:loading-dots"></span>
              <span v-else>{{ actionButtonLabel }}</span>
            </button>
          </slot>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>
